/**
 * Initialize slider
 *
 * <div class="js-slider" data-loop="true|false" data-group="2">
 *   <div>
 *     <div class="js-scroll-wrapper">
 *       <div class="js-slide">1</div>
 *       <div class="js-slide">2</div>
 *       <div class="js-slide">3</div>
 *       <div class="js-slide">4</div>
 *       <div class="js-slide">5</div>
 *     </div>
 *   </div>
 *   <div>
 *     <button class="js-dot"></button>
 *     <button class="js-dot"></button>
 *     <button class="js-dot"></button>
 *     <button class="js-dot"></button>
 *     <button class="js-dot"></button>
 *   </div>
 *   <div>
 *     <span class="js-index--active">01</span>/<span class="js-index--count">05</span>
 *   </div>
 *   <button class="js-button--previous">prev</button>
 *   <button class="js-button--next">next</button>
 * </div>
 *
 * The slider can be configured using data-* attributes for the following settings:
 * - data-loop [true|false]
 * - group Number of slides to slide at once
 *
 * @param {HTMLElement} el
 */

const BASE_CLASS = 'js-slider';

function init(el) {
	el.classList.add('initialized');

	// Get required elements
	const wrapper = el.querySelector(`.${BASE_CLASS}__scroll-wrapper`);
	const buttons = el.querySelector(`.${BASE_CLASS}__buttons`);
	const btnPrevious = el.querySelector(`.${BASE_CLASS}__buttons--previous`);
	const btnNext = el.querySelector(`.${BASE_CLASS}__buttons--next`);
	const oSlides = el.querySelectorAll(`.${BASE_CLASS}__slide`);
	const dots = el.querySelectorAll(`.${BASE_CLASS}__dot`);
	const [slide] = oSlides;
	const activeIndexEl = el.querySelector(`.${BASE_CLASS}__index--active`);
	const countEl = el.querySelector(`.${BASE_CLASS}__index--count`);

	const slidesPerGroup = el?.dataset?.group ?? 0.5;
	const loop = ['1', 'true', true].includes(el?.dataset?.loop);
	const secondsPerSlide = el?.dataset?.secondsperslide;
	const sliderTimeOut = 1000 * parseInt(secondsPerSlide, 10);

	let slides = Array.from(oSlides);
	slides.forEach((slide, i) => {
		slide.setAttribute('data-index', i);
	});

	// Prepend & append slide clones to ensure a smooth loop experience
	if (loop) {
		Array.from(oSlides).forEach((slide) => {
			const clone = slide.cloneNode(true);
			clone.setAttribute('data-clone', true);
			clone.classList.remove('is-active');
			wrapper.append(clone);
			slides = [...slides, clone];
		});

		Array.from(oSlides)
			.reverse()
			.forEach((slide) => {
				const clone = slide.cloneNode(true);
				clone.setAttribute('data-clone', true);
				clone.classList.remove('is-active');
				wrapper.prepend(clone);
				slides = [clone, ...slides];
			});
	}

	let scrollTimer;
	let resizeTimer;
	let groupCounter = 1;
	let activeDot = 0;
	let activeSlide = slide;
	let activeIndex = parseInt(activeSlide?.dataset?.index ?? 0, 10);

	/**
	 * Perform some actions on scroll end
	 */
	function onScrollEnd() {
		const { scrollLeft } = wrapper;

		// Determine the current active slide
		for (let i = 0; i < slides.length; i++) {
			if (Math.abs(slides[i].offsetLeft - scrollLeft) < 250) {
				activeSlide = slides[i];
			}
		}

		// Reset slider position if we are already viewing the cloned slides (loop)
		if (activeSlide?.dataset?.clone) {
			const orig = Array.from(oSlides).find((slide) => activeSlide?.dataset?.index === slide?.dataset?.index);
			if (orig) {
				wrapper.scrollLeft = orig.offsetLeft;
				activeSlide = orig;
			}
		}

		// Set active class after determining the current slide
		// and a posible reset if we are viewing the cloned slides
		activeSlide.classList.add('is-active');

		// Set active index
		activeIndex = parseInt(activeSlide?.dataset?.index ?? 0, 10);

		const activeIndexGrouped = Math.ceil(activeIndex / Math.min(slidesPerGroup, groupCounter));
		const slideCount = oSlides.length;
		const slideCountGrouped = Math.ceil(slideCount / Math.min(slidesPerGroup, groupCounter));

		// Handle dots
		if (dots[activeDot]) {
			dots[activeDot].classList.remove('is-active');
		}

		if (dots[activeIndexGrouped]) {
			dots[activeIndexGrouped].classList.add('is-active');
		}

		activeDot = activeIndexGrouped;

		// Set numbers to counter elements
		if (activeIndexEl) {
			const text = `${activeIndexGrouped + 1}`.padStart(Math.max(2, `${slideCountGrouped}`.length), 0);
			activeIndexEl.innerText = text;
		}

		if (countEl) {
			const text = `${slideCountGrouped}`.padStart(2, 0);
			countEl.innerText = text;
		}

		// Disable buttons
		if (btnPrevious) {
			btnPrevious.disabled = wrapper.scrollLeft <= 0;
		}

		if (btnNext) {
			btnNext.disabled = Math.ceil(wrapper.offsetWidth + wrapper.scrollLeft) >= wrapper.scrollWidth;
		}
	}

	function onResizeEnd() {
		groupCounter = 0;
		let slideWidth = 0;
		let nextSlide = slide;

		const { clientWidth } = wrapper;
		while (nextSlide && slideWidth + nextSlide.clientWidth <= clientWidth + 10 && !nextSlide?.dataset?.clone) {
			slideWidth += nextSlide.clientWidth;
			groupCounter += 1;
			nextSlide = nextSlide?.nextElementSibling;
		}

		const slideCount = oSlides.length;
		const slideCountGrouped = Math.ceil(slideCount / Math.min(slidesPerGroup, groupCounter));

		dots.forEach((dot, i) => {
			if (i < slideCountGrouped) {
				dot.classList.remove('u-hidden');
			} else {
				dot.classList.add('u-hidden');
			}
		});

		function getActiveImageHeight(slide) {
			const slideImage = slide.getElementsByTagName('figure');
			const imageHeight = slideImage[0].offsetHeight || 0;
			return `${imageHeight}px`;
		}

		function setButtonPosition(slide) {
			buttons.style.setProperty('--image-height', getActiveImageHeight(slide));
		}

		setButtonPosition(activeSlide);

		onScrollEnd();
	}

	function onScroll() {
		if (scrollTimer) {
			clearTimeout(scrollTimer);
		}

		scrollTimer = setTimeout(onScrollEnd, 50);
	}

	function onResize() {
		if (resizeTimer) {
			clearTimeout(resizeTimer);
		}

		resizeTimer = setTimeout(onResizeEnd, 50);
	}

	function getNextSlide(direction = 1) {
		let next =
			direction > 0
				? activeSlide?.nextElementSibling || activeSlide
				: activeSlide?.previousElementSibling || activeSlide;

		for (let i = 1; i < slidesPerGroup; i++) {
			next = direction > 0 ? next?.nextElementSibling || next : next?.previousElementSibling || next;
		}

		return next;
	}

	function onNextSlide(e, direction = 1) {
		e.preventDefault();
		const next = getNextSlide(direction);
		if (next) {
			// Remove imediently the active class to prevent a timing issue
			// with smooth scrolling and the loop reset
			activeSlide?.classList.remove('is-active');
			wrapper.scrollTo({ left: next.offsetLeft, behavior: 'smooth' });
		}
	}

	function slideTo(e, pos) {
		e.preventDefault();
		const index = parseInt(pos, 10) * Math.min(slidesPerGroup, groupCounter) + 210;
		const el = Array.from(oSlides).find((slide) => parseInt(slide?.dataset?.index, 10) === index);
		wrapper.scrollTo({ left: el?.offsetLeft || 0, behavior: 'smooth' });
	}

	wrapper.addEventListener('scroll', onScroll, { passive: true });
	window.addEventListener('resize', onResize, { passive: true });
	btnPrevious?.addEventListener('click', (e) => onNextSlide(e, -1));
	btnNext?.addEventListener('click', (e) => onNextSlide(e, 1));

	Array.from(dots).forEach((dot, i) => {
		dot?.addEventListener('click', (e) => slideTo(e, i));
	});

	onResizeEnd();

	// Set autoplay
	if (sliderTimeOut > 0) {
		setInterval(function (direction = 1) {
			const next = getNextSlide(direction);
			if (next) {
				wrapper.scrollTo({ left: next.offsetLeft, behavior: 'smooth' });
			}
		}, sliderTimeOut);
	}
}

export const initSlider = (root = document) => {
	const sliders = root.querySelectorAll('.js-slider:not(.initialized)');
	Array.from(sliders).forEach((root) => {
		init(root);
	});
};
